import { cn } from '@/lib/utils';
import Image from 'next/image';
import React, { useState } from 'react';

type AvatarProps = {
  name?: string;
  image?: string;
  size?: number;
  bordered?: boolean;
  borderColor?: 'primary' | 'secondary' | 'accent';
} & React.HTMLAttributes<HTMLDivElement>;

const Avatar: React.FC<AvatarProps> = ({ 
  className, 
  bordered, 
  borderColor = 'primary', 
  image, 
  name, 
  size = 10, 
  ...props 
}) => {
  const [imageError, setImageError] = useState(false);
  // Convert size number to Tailwind classes
  const sizeClasses = {
    4: 'h-4 w-4',
    5: 'h-5 w-5',
    6: 'h-6 w-6',
    7: 'h-7 w-7',
    8: 'h-8 w-8',
    9: 'h-9 w-9',
    10: 'h-10 w-10',
    11: 'h-11 w-11',
    12: 'h-12 w-12',
    14: 'h-14 w-14',
    16: 'h-16 w-16',
    20: 'h-20 w-20',
    24: 'h-24 w-24',
  }[size] || 'h-10 w-10';

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-full overflow-hidden bg-gray-500',
        {
          'border-2 border-solid': bordered,
          'border-primary': bordered && borderColor === 'primary',
          'border-secondary': bordered && borderColor === 'secondary',
          'border-accent': bordered && borderColor === 'accent',
        },
        sizeClasses,
        className
      )}
      {...props}
    >
      {image && !imageError ? (
        <Image 
          src={image} 
          alt={name || 'avatar'} 
          width={size * 4} 
          height={size * 4}
          className="object-cover w-full h-full"
          onError={() => setImageError(true)}
        />
      ) : (
        <span className="text-xs font-medium leading-none text-white">
          {name?.charAt(0).toUpperCase() || '?'}
        </span>
      )}
    </span>
  );
};

export { Avatar };
export default Avatar;